import moment from "moment";

export const parseTime = (time) => {
  const t = new Date(time).toLocaleTimeString("en-GB").split(":");
  return `${t[0]}:${t[1]}`;
};
export const parseDate = (date) => {
  const newDate = new Date(date);
  return newDate.toLocaleString("en-GB");
};

export const getTimeZoneOffset = (timeZone = "Europe/London") => {
  const now = moment.tz(timeZone);
  const offsetMinutes = now.utcOffset();
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const minutes = Math.abs(offsetMinutes) % 60;
  const sign = offsetMinutes < 0 ? "-" : "+";
  return `${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )}`;
};

/**
 * Format a Date object as a YYYY-MM-DD string in LOCAL time.
 *
 * Why not `toISOString().slice(0, 10)`? — that's the timezone bug we used
 * to ship. `toISOString` converts to UTC first; in any TZ east of UTC
 * (e.g. BST = UTC+1 from late March to late October) the local-midnight
 * Date sits at the PREVIOUS day's 23:00 UTC, and the resulting string is
 * one day behind. That caused `maxAllowedDate(0)` to return yesterday
 * instead of today, which made today un-selectable on every Vuetify
 * v-date-picker bound to it AND made manually-typed today-dates fail
 * the `validateDate` upper-bound check ("Date must be on or before
 * <yesterday>"). Reported on the admin Documentation edit screen
 * (Issue Date for carer liability insurance, 2026-05-28).
 *
 * Using getFullYear/getMonth/getDate keeps the date in the user's local
 * frame of reference, which is what every consumer here expects.
 */
const toLocalISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};

export const minAllowedDobDate = () => {
  const d = new Date();
  return toLocalISODate(
    new Date(d.getFullYear() - 16, d.getMonth(), d.getDate())
  );
};

export const minAllowedDate = (date) => {
  if (date && typeof date === "string") return toLocalISODate(new Date(date));
  const minYear = date > -1 ? date : 100;
  const d = new Date();
  return toLocalISODate(
    new Date(d.getFullYear() - minYear, d.getMonth(), d.getDate())
  );
};

export const maxAllowedDate = (date) => {
  if (date && typeof date === "string") return toLocalISODate(new Date(date));
  const maxYear = date > -1 ? date : 100;
  const d = new Date();
  return toLocalISODate(
    new Date(d.getFullYear() + maxYear, d.getMonth(), d.getDate())
  );
};

export const formatDatePicker = (value, dateFormat = "DD-MM-YYYY") => {
  const date = (value || "").replace(/[^\w\s]/gi, "-");
  moment.locale("en-GB");
  const momentDate = moment(date, dateFormat);
  if (momentDate.isValid() && date.split("-").length === 3) {
    return momentDate.format("YYYY-MM-DD");
  }
  return date;
};

export const validateDate = (value, { minDate, maxDate }) => {
  if (!value) return "Enter Valid Date in DD/MM/YYYY Format.";
  const dashSplit = value.split("-");
  const slashSplit = value.split("/");

  let formattedDate = null;
  if (dashSplit.length === 3) {
    const [year, month, day] = dashSplit.map(Number);
    if (day > 0 && month > 0 && year > 0 && year.toString().length === 4) {
      formattedDate = formatDatePicker(value, "YYYY-MM-DD");
    } else return "Select Valid Date from Calender";
  } else if (slashSplit.length === 3) {
    const [day, month, year] = slashSplit.map(Number);
    if (day > 0 && month > 0 && year > 0 && year.toString().length === 4) {
      formattedDate = moment(value, "DD/MM/YYYY").format("YYYY-MM-DD");
    } else return "Enter Valid Date in DD/MM/YYYY Format";
  } else {
    return "Enter Valid Date Completely";
  }
  const inputDate = moment(formattedDate).startOf("day");
  const minDateMoment = moment(minDate).startOf("day");
  const maxDateMoment = moment(maxDate).startOf("day");
  if (inputDate < minDateMoment)
    return `Date must be on or after ${minDateMoment.format("DD/MM/YYYY")}`;
  if (inputDate > maxDateMoment)
    return `Date must be on or before ${maxDateMoment.format("DD/MM/YYYY")}`;
  return true;
};

export function formatToMMYYYY(value) {
  let formatted = value.replace(/\D/g, "");
  if (formatted.length > 2) {
    formatted = `${formatted.slice(0, 2)}/${formatted.slice(2, 6)}`;
  }
  return formatted;
}

export function isValidMMYYYY(value) {
  const regex = /^(0[1-9]|1[0-2])\/\d{4}$/;
  return regex.test(value);
}

export const MMYYYYRule = [
  (value) => !!value || "Completion Year is required.",
  (value) => isValidMMYYYY(value) || "Enter a valid MM/YYYY format.",
];
